import { useCallback, useEffect, useRef } from 'react';
import {
  Redirect,
  Route,
  Switch,
  useHistory,
  useLocation,
  useParams,
  useRouteMatch,
} from 'react-router-dom';
import { DateTime } from 'luxon';

import { useGetPatientQuery } from '~/store/services/patients';
import { useGetPatientClinicalProfileQuery } from '~/store/services/clinicalData';
import PatientAppointmentsTab from '~/components/maud/patients/profile/tabs/appointments/PatientAppointmentsTab';
import { CalendarListTypeOption } from '~/lib/constants/appointment.constants';
import ScheduleDayView from '~/components/common/calendar/schedule/day-view/ScheduleDayView';
import ScheduleWeekView from '~/components/common/calendar/schedule/week-view/ScheduleWeekView';
import PatientGroupClassesListView from '~/components/maud/patients/profile/tabs/appointments/groupClasses';

const VALID_SUB_VIEWS = new Set<string>([
  CalendarListTypeOption.DAILY_VIEW,
  CalendarListTypeOption.WEEKLY_VIEW,
  CalendarListTypeOption.GROUPS,
]);

const AppointmentsPage = () => {
  const { patientId } = useParams<{ patientId: string }>();
  const { path, url } = useRouteMatch();
  const history = useHistory();
  const location = useLocation();

  const subViewMatch = useRouteMatch<{ subView: string }>({
    path: `${path}/:subView`,
    exact: true,
  });

  const activeView: AppointmentListType = VALID_SUB_VIEWS.has(
    subViewMatch?.params?.subView,
  )
    ? (subViewMatch.params.subView as AppointmentListType)
    : CalendarListTypeOption.DAILY_VIEW;

  const handleViewChange = (view: AppointmentListType, date?: string) => {
    if (view === CalendarListTypeOption.GROUPS) {
      history.push(`${url}/${view}`);
      return;
    }
    const isoDate = date ? DateTime.fromISO(date).toISODate() : null;
    if (!isoDate) {
      history.push(`${url}/${view}${window.location.search}`);
      return;
    }
    const params = new URLSearchParams(window.location.search);
    params.set('date', isoDate);
    history.push(`${url}/${view}?${params}`);
  };

  const mountSyncComplete = useRef(false);

  useEffect(() => {
    const id = setTimeout(() => {
      mountSyncComplete.current = true;
    }, 0);
    return () => clearTimeout(id);
  }, []);

  const handleDateChange = useCallback((date: string) => {
    const isoDate = DateTime.fromISO(date).toISODate();
    if (!isoDate) return;
    const currentParams = new URLSearchParams(window.location.search);
    if (currentParams.get('date') === isoDate) return;
    const params = new URLSearchParams(window.location.search);
    params.set('date', isoDate);
    const method = mountSyncComplete.current ? 'pushState' : 'replaceState';
    window.history[method](null, '', `${window.location.pathname}?${params}`);
  }, []);

  const { data: patient } = useGetPatientQuery(
    { patientId },
    { skip: !patientId },
  );
  const { data: patientClinicalProfile } = useGetPatientClinicalProfileQuery(
    patientId,
    { skip: !patientId },
  );

  if (!patient) return null;

  return (
    <PatientAppointmentsTab
      listType={activeView}
      onDateChange={handleDateChange}
      onViewChange={handleViewChange}
      patient={patient}
      patientClinicalProfile={patientClinicalProfile}
      patientId={patientId}
    >
      {(scheduleProps) => (
        <Switch>
          <Route exact path={`${path}/${CalendarListTypeOption.DAILY_VIEW}`}>
            <ScheduleDayView {...scheduleProps} />
          </Route>
          <Route exact path={`${path}/${CalendarListTypeOption.WEEKLY_VIEW}`}>
            <ScheduleWeekView {...scheduleProps} />
          </Route>
          <Route exact path={`${path}/${CalendarListTypeOption.GROUPS}`}>
            <PatientGroupClassesListView patientId={patientId} />
          </Route>
          <Redirect
            to={`${url}/${CalendarListTypeOption.DAILY_VIEW}${location.search}`}
          />
        </Switch>
      )}
    </PatientAppointmentsTab>
  );
};

export default AppointmentsPage;
